import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Plus, Edit, Trash2, X, Check, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { usersAPI, CreateUserPayload, UpdateUserPayload } from '@/api/users';
import { User } from '@/types/auth';
import { Button, Input, Loading, ErrorMessage } from '@/components/common';
import { formatDate } from '@/utils/formatters';
import { ROLE_LABELS } from '@/utils/constants';

interface UserFormData {
  username: string;
  password?: string;
  role: 'call_center' | 'accountant' | 'manager' | 'driver';
  canExport: boolean;
  isActive: boolean;
  englishName?: string;
  arabicName?: string;
}

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, reset, formState: { errors }, setValue, control } = useForm<UserFormData>({
    defaultValues: {
      role: 'call_center',
      canExport: false,
      isActive: true
    }
  });

  const selectedRole = useWatch({ control, name: 'role' });

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await usersAPI.getAll();
      setUsers(response.data.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'فشل تحميل المستخدمين');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setValue('username', user.username);
      setValue('role', user.role);
      setValue('canExport', user.canExport);
      setValue('isActive', user.isActive);
      setValue('password', ''); // Don't fill password
    } else {
      setEditingUser(null);
      reset({
        username: '',
        password: '',
        role: 'call_center',
        canExport: false,
        isActive: true,
        englishName: '',
        arabicName: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    reset();
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      setIsSubmitting(true);

      // Validate driver-specific fields
      if (data.role === 'driver') {
        if (!data.englishName || !data.arabicName) {
          toast.error('الاسم بالإنجليزية والعربية مطلوبان للسائقين');
          setIsSubmitting(false);
          return;
        }
      }

      if (editingUser) {
        // Update
        const payload: UpdateUserPayload = {
          role: data.role,
          canExport: data.canExport,
          isActive: data.isActive
        };
        if (data.password) payload.password = data.password;
        if (data.role === 'driver') {
          payload.englishName = data.englishName;
          payload.arabicName = data.arabicName;
        }
        
        await usersAPI.update(editingUser.id, payload);
        toast.success('تم تحديث المستخدم بنجاح');
      } else {
        // Create
        if (!data.password) {
            toast.error('كلمة المرور مطلوبة للمستخدمين الجدد');
            setIsSubmitting(false);
            return;
        }
        const payload: CreateUserPayload = {
          username: data.username,
          password: data.password,
          role: data.role,
          canExport: data.canExport
        };
        if (data.role === 'driver') {
          payload.englishName = data.englishName;
          payload.arabicName = data.arabicName;
        }
        await usersAPI.create(payload);
        toast.success('تم إنشاء المستخدم بنجاح');
      }
      handleCloseModal();
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشلت العملية');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    
    try {
      await usersAPI.delete(id);
      toast.success('تم حذف المستخدم بنجاح');
      fetchUsers();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'فشل حذف المستخدم');
    }
  };

  if (isLoading) return <Loading />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-primary">إدارة المستخدمين</h1>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 ml-2" />
          إضافة مستخدم
        </Button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>اسم المستخدم</th>
                <th>الدور</th>
                <th>الحالة</th>
                <th>صلاحية التصدير</th>
                <th>تاريخ الإنشاء</th>
                <th className="text-center">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="font-semibold text-white">{user.username}</td>
                  <td className="text-gray-300">{ROLE_LABELS[user.role]}</td>
                  <td>
                    {user.isActive ? (
                      <span className="badge badge-success">
                        نشط
                      </span>
                    ) : (
                      <span className="badge badge-danger">
                        غير نشط
                      </span>
                    )}
                  </td>
                  <td>
                    {user.canExport ? <Check className="h-4 w-4 text-success" /> : <XCircle className="h-4 w-4 text-gray-500" />}
                  </td>
                  <td className="text-gray-300">{formatDate(user.createdAt)}</td>
                  <td className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleOpenModal(user)}
                        className="text-info hover:text-blue-400 transition-colors"
                        title="تعديل"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-danger hover:text-red-400 transition-colors"
                        title="حذف"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-dark border border-gray-700 rounded-lg p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-6 border-b border-gray-700 pb-4">
              <h2 className="text-xl font-bold text-primary">{editingUser ? 'تعديل مستخدم' : 'إضافة مستخدم'}</h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {!editingUser && (
                  <Input
                    label="اسم المستخدم"
                    {...register('username', { required: 'اسم المستخدم مطلوب' })}
                    error={errors.username?.message}
                    className="text-right"
                  />
              )}
              
              <Input
                label={editingUser ? "كلمة المرور (اتركها فارغة للإبقاء على الحالية)" : "كلمة المرور"}
                type="password"
                {...register('password')}
                error={errors.password?.message}
                className="text-right"
              />

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">الدور</label>
                <select
                  {...register('role')}
                  className="w-full px-4 py-2 rounded-lg bg-dark-lighter border border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                >
                  <option value="call_center">مركز الاتصال</option>
                  <option value="accountant">محاسب</option>
                  <option value="manager">مدير</option>
                  <option value="driver">سائق</option>
                </select>
              </div>

              {selectedRole === 'driver' && (
                <>
                  <Input
                    label="الاسم بالإنجليزية"
                    {...register('englishName', { 
                      required: selectedRole === 'driver' ? 'الاسم بالإنجليزية مطلوب للسائقين' : false 
                    })}
                    error={errors.englishName?.message}
                    className="text-right"
                    placeholder="John Doe"
                  />
                  
                  <Input
                    label="الاسم بالعربية"
                    {...register('arabicName', { 
                      required: selectedRole === 'driver' ? 'الاسم بالعربية مطلوب للسائقين' : false 
                    })}
                    error={errors.arabicName?.message}
                    className="text-right"
                    placeholder="محمد أحمد"
                  />
                </>
              )}

              <div className="flex flex-col gap-3 pt-2">
                 <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="canExport"
                      {...register('canExport')}
                      className="w-4 h-4 rounded border-gray-600 text-primary focus:ring-primary bg-dark-lighter"
                    />
                    <label htmlFor="canExport" className="mr-2 block text-sm text-gray-300 select-none cursor-pointer">
                      صلاحية التصدير
                    </label>
                 </div>

                 <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      {...register('isActive')}
                      className="w-4 h-4 rounded border-gray-600 text-primary focus:ring-primary bg-dark-lighter"
                    />
                    <label htmlFor="isActive" className="mr-2 block text-sm text-gray-300 select-none cursor-pointer">
                      نشط
                    </label>
                 </div>
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-700">
                <Button type="button" variant="secondary" onClick={handleCloseModal}>
                  إلغاء
                </Button>
                <Button type="submit" isLoading={isSubmitting}>
                  {editingUser ? 'حفظ التغييرات' : 'إنشاء المستخدم'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
